import { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import BoardLayout from '@/components/layout/BoardLayout'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'
import ChatInterface from '@/components/ChatInterface'
import AiChatButton from '@/components/AiChatButton'
import DatasheetPreview from '@/components/DatasheetPreview'
import { toast } from 'sonner'
import clsx from 'clsx'
import { X, Database, File, ChevronRight, PanelsTopLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUIv2 } from '@/hooks/useUIv2'
import AppSidebar from '@/components/AppSidebar'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'

const Datasheet = () => {
  const isV2 = useUIv2()
  const { doc_id } = useParams()
  const { t } = useTranslation(['data_catalog', 'document_viewer'])
  const [searchParams] = useSearchParams()
  // boardName comes only from the ?boardName= query param (set when navigating
  // from a board). Documents opened from the Data Catalog have no param, so the
  // breadcrumb shows "Data Catalog" rather than the active/Master board.
  const boardNameParam = searchParams.get('boardName')
  const docNameParam = searchParams.get('docName')
  const navigateBack = useNavigateBack()
  const navigate = useNavigate()

  // Detect if user came from a board
  const cameFromBoard = typeof window !== 'undefined' && window.document.referrer && (() => {
    try { return new URL(window.document.referrer).pathname.startsWith('/board/') } catch { return false }
  })()
  const boardName = boardNameParam || (cameFromBoard ? t('document_viewer:common.board', { defaultValue: 'Board' }) : null)
  const { setContextId } = useAppStore()
  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [chatOpen, setChatOpen] = useState(false)

  useEffect(() => {
    const fetchDocument = async () => {
      if (!doc_id) return
      try {
        const doc = await apiService.getDocument(doc_id)
        setDocument(doc)
        setContextId(doc_id)
      } catch {
        toast.error(t('document_viewer:common.fetchError', { defaultValue: 'Error fetching document' }))
      }
    }
    fetchDocument()
  }, [doc_id, setContextId])

  const handleChatOpen = () => {
    setChatOpen(!chatOpen)
  }

  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    if (!document) return
    void renameDocument(doc_id, newName, {
      previousName: document.name,
      applyName: (n) => setDocument((d) => (d ? { ...d, name: n } : d)),
    })
  }

  const closeAction = {
    onClick: navigateBack,
    element: (
      <button
        type="button"
        className="p-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
        title={t('common:buttons.close')}
      >
        <X className="w-5 h-5" />
      </button>
    ),
  }

  // ── V2 layout: AppSidebar + breadcrumb + full DatasheetPreview ──
  if (isV2) {
    return (
      <div className="flex h-screen">
        <AppSidebar />
        <div className="flex flex-1 flex-col min-w-0">
          {/* Breadcrumb navbar */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                onClick={boardName ? navigateBack : () => navigate('/data-catalog')}
                className="flex items-center gap-2 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                {boardName ? (
                  <PanelsTopLeft className="w-[18px] h-[18px]" />
                ) : (
                  <Database className="w-[18px] h-[18px]" />
                )}
                <span className="text-base">{boardName || t('nav:menu.data', 'Data')}</span>
              </button>
              <ChevronRight className="w-[15px] h-[15px] text-muted-foreground shrink-0" />
              <div className="flex items-center gap-2 min-w-0">
                <File className="w-[18px] h-[18px] text-foreground shrink-0" />
                {document?.name || docNameParam ? (
                  <FileNameWithCopy
                    name={document?.name || docNameParam}
                    className="text-base font-normal"
                    onRename={document ? handleRename : undefined}
                  />
                ) : (
                  <span className="text-base text-foreground truncate">{t('document_viewer:common.loading', { defaultValue: 'Loading...' })}</span>
                )}
              </div>
            </div>
          </div>

          {/* Spreadsheet viewer fills remaining space */}
          <div className="flex-1 min-h-0">
            <DatasheetPreview docId={doc_id!} className="h-full" />
          </div>
        </div>
      </div>
    )
  }

  // ── Legacy layout ──
  return (
    <BoardLayout
      name={boardName || t('nav:menu.data', 'Data')}
      backUrl={''}
      handleBackCallback={navigateBack}
      actions={[closeAction]}
    >
      <div className="flex h-full w-full">
        <div
          className={clsx(
            'h-full relative transition-all',
            chatOpen ? 'w-8/12' : 'w-full'
          )}
        >
          <div className="h-full flex flex-col gap-4 p-4 min-h-0">
            <div className="shrink-0 flex flex-col gap-1">
              {document?.name ? (
                <FileNameWithCopy name={document.name} onRename={handleRename} />
              ) : (
                <h2 className="text-lg font-semibold">{t('document_viewer:common.loading', { defaultValue: 'Loading...' })}</h2>
              )}
              <div className="text-sm text-gray-500 dark:text-dark-text-muted flex gap-4">
                {document?.properties?.sheet_count && (
                  <span>{t('document_viewer:datasheet.sheets', { count: document.properties.sheet_count, defaultValue: 'Sheets: {{count}}' })}</span>
                )}
                {document?.properties?.row_count && (
                  <span>{t('document_viewer:datasheet.totalRows', { count: document.properties.row_count, defaultValue: 'Total rows: {{count}}' })}</span>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <DatasheetPreview docId={doc_id!} />
            </div>
          </div>
          <AiChatButton isOpen={chatOpen} onClick={handleChatOpen} />
        </div>
        <div
          className={clsx(
            'h-full overflow-hidden transition-all border-l border-gray-100 dark:border-dark-border',
            chatOpen ? 'w-4/12' : 'w-0'
          )}
        >
          {doc_id && (
            <ChatInterface
              context_id={doc_id}
              context_type="document"
              onClose={handleChatOpen}
              chatMode="canvas"
            />
          )}
        </div>
      </div>
    </BoardLayout>
  )
}

export default Datasheet
